import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  Alert,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import Select, {
  components,
  type GroupBase,
  type OptionProps,
  type SingleValueProps,
} from "react-select";
import CreatableSelect from "react-select/creatable";
import axiosInstance from "../../../axiosSetup";
import { toast } from "react-toastify";
import "./RuleEngineConfig.css";
import { useSearchParams } from "react-router-dom";

interface SegmentDto {
  segments: any;
  ps: any;
  fields: any;
}

interface FieldMapping {
  id?: number | null;
  protocolId?: number | null;
  sourceFieldId?: number | null;
  targetFieldId?: number | null;
  mappingType?: string | null;
  lookupType?: string | null;
}

interface SegmentsAndMappingsResponse {
  segments: SegmentDto[];
  fieldMappings: FieldMapping[];
}

interface FieldInfo {
  field: any;
  segment: any;
}

const isInactiveMapping = (mappingType: string | null | undefined): boolean =>
  (mappingType ?? "").toUpperCase() === "INACTIVE";

const usesTargetAsSource = (mappingType: string | null | undefined): boolean => {
  const upper = (mappingType ?? "").toUpperCase();
  return upper === "FIXED" || upper === "STATIC";
};

const MAPPING_TYPES_SOURCE_AS_TARGET = new Set(["PIN"]);

const usesSourceFieldAsTarget = (
  mappingType: string | null | undefined
): boolean => MAPPING_TYPES_SOURCE_AS_TARGET.has((mappingType ?? "").toUpperCase());

const syncSourceFromTarget = (row: FieldMapping): FieldMapping => {
  if (
    usesTargetAsSource(row.mappingType) &&
    typeof row.targetFieldId === "number"
  ) {
    return { ...row, sourceFieldId: row.targetFieldId };
  }
  return row;
};

interface MappingTypeOption {
  value: string;
  label: string;
  description: string;
}

const MAPPING_TYPE_OPTIONS: MappingTypeOption[] = [
  {
    value: "DIRECT",
    label: "DIRECT",
    description:
      "Copies the source field value directly to the target field.",
  },
  {
    value: "LOOKUP",
    label: "LOOKUP",
    description:
      "Fetches a value from a lookup source and maps it to the target field.",
  },
  {
    value: "FIXED",
    label: "FIXED",
    description: "Assigns a constant, predefined value to the target field.",
  },
  {
    value: "IF_PRESENT",
    label: "IF_PRESENT",
    description: "Maps the value only if it exists and is not blank.",
  },
  {
    value: "STATIC",
    label: "STATIC",
    description:
      "Sets a constant value and marks the field as static (relevant for EDIFACT generation).",
  },
  {
    value: "DATE",
    label: "DATE",
    description:
      "Formats a date value using provided pattern. (uses current date/time if pattern starts with NOW).",
  },
  {
    value: "SPLIT",
    label: "SPLIT",
    description: "Splits a string into a list using a delimiter.",
  },
  {
    value: "TRANSFORM",
    label: "TRANSFORM",
    description:
      "Applies custom developer-defined logic to compute the field value.",
  },
  {
    value: "FILTER",
    label: "FILTER",
    description:
      "Excludes records or values that do not match a specified condition/pattern (can be used on arrays).",
  },
  {
    value: "PIN",
    label: "PIN",
    description:
      "Ensures the target value matches the source value; otherwise drops the record.",
  },
  {
    value: "MATCH",
    label: "MATCH",
    description:
      "Extracts values that match a pattern; non-matching values may be set to null (can be used on arrays).",
  },
  {
    value: "EXTRACT",
    label: "EXTRACT",
    description:
      "Applies a regex pattern (lookup type) to the source value and maps the first capture group to the target field.",
  },
  {
    value: "INACTIVE",
    label: "INACTIVE",
    description:
      "Disables the mapping so it is ignored during processing.",
  },
];

const mappingTypeDescriptionByValue = new Map(
  MAPPING_TYPE_OPTIONS.map((o) => [o.value, o.description])
);

const getMappingTypeDescription = (
  mappingType: string | null | undefined
): string | undefined => {
  if (!mappingType?.trim()) return undefined;
  return mappingTypeDescriptionByValue.get(mappingType.toUpperCase());
};

const toMappingTypeOption = (mappingType: string): MappingTypeOption => {
  const upper = mappingType.toUpperCase();
  const known = MAPPING_TYPE_OPTIONS.find((o) => o.value === upper);
  return known ?? { value: upper, label: upper, description: "" };
};

const SELECT_CLASS_PREFIX = "rec-select";

const fieldSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 9999,
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    backgroundColor: "#fff",
    zIndex: 9999,
  }),
  menuList: (base: Record<string, unknown>) => ({
    ...base,
    backgroundColor: "#fff",
  }),
  control: (base: Record<string, unknown>, state: { isDisabled?: boolean }) => ({
    ...base,
    borderColor: state.isDisabled ? "#ced4da" : "#adb5bd",
    backgroundColor: state.isDisabled ? "#f8f9fa" : "#fff",
  }),
  singleValue: (base: Record<string, unknown>) => ({
    ...base,
    color: "#212529",
  }),
  input: (base: Record<string, unknown>) => ({
    ...base,
    color: "#212529",
  }),
  placeholder: (base: Record<string, unknown>) => ({
    ...base,
    color: "#6c757d",
  }),
  option: (
    base: Record<string, unknown>,
    state: { isDisabled?: boolean; isFocused?: boolean; isSelected?: boolean }
  ) => ({
    ...base,
    whiteSpace: "normal" as const,
    color: state.isSelected ? "#fff" : "#212529",
    backgroundColor: state.isSelected
      ? "#0d6efd"
      : state.isFocused
      ? "#e7f1ff"
      : "#fff",
    cursor: state.isDisabled ? "not-allowed" : "default",
  }),
};

const MappingTypeOptionLabel: React.FC<
  OptionProps<MappingTypeOption, false, GroupBase<MappingTypeOption>>
> = (props) => (
  <components.Option {...props}>
    <div className="rec-select-option-title">{props.data.label}</div>
    {props.data.description ? (
      <small className="rec-select-option-desc d-block mt-1">
        {props.data.description}
      </small>
    ) : null}
  </components.Option>
);

const mappingTypeSelectComponents = {
  Option: MappingTypeOptionLabel,
};

const MappingTypeReference: React.FC = () => (
  <Accordion className="mb-3 rec-mapping-type-ref">
    <Accordion.Item eventKey="0">
      <Accordion.Header>Mapping type reference</Accordion.Header>
      <Accordion.Body className="py-2 px-3">
        <dl className="mb-0 small rec-mapping-type-ref-list">
          {MAPPING_TYPE_OPTIONS.map((opt) => (
            <div key={opt.value} className="rec-mapping-type-ref-item mb-2">
              <dt className="rec-mapping-type-ref-term">{opt.label}</dt>
              <dd className="rec-mapping-type-ref-desc mb-0">{opt.description}</dd>
            </div>
          ))}
        </dl>
      </Accordion.Body>
    </Accordion.Item>
  </Accordion>
);

const MappingTypeSelect: React.FC<{
  value: string | null | undefined;
  disabled?: boolean;
  menuPortalTarget?: HTMLElement;
  onChange: (value: string) => void;
}> = ({ value, disabled, menuPortalTarget, onChange }) => {
  const selectValue = value?.trim() ? toMappingTypeOption(value) : null;
  const description = getMappingTypeDescription(value);

  return (
    <>
      <CreatableSelect<MappingTypeOption, false>
        classNamePrefix={SELECT_CLASS_PREFIX}
        placeholder="Select or type mapping type"
        isClearable
        isDisabled={disabled}
        menuPortalTarget={menuPortalTarget}
        menuPosition="fixed"
        styles={fieldSelectStyles}
        components={mappingTypeSelectComponents}
        options={MAPPING_TYPE_OPTIONS}
        value={selectValue}
        formatCreateLabel={(input) => `Use custom type: ${input.toUpperCase()}`}
        onChange={(selected) => onChange(selected?.value ?? "")}
        onCreateOption={(input) => onChange(input)}
      />
      {description ? (
        <small className="rec-mapping-type-hint d-block mt-1">{description}</small>
      ) : value?.trim() ? (
        <small className="rec-empty-hint d-block mt-1">
          Custom mapping type (no built-in description).
        </small>
      ) : null}
    </>
  );
};

const getFieldType = (field: any): string => {
  if (!field) return "";
  const raw =
    field.fieldType ??
    field.field_type ??
    field.FIELD_TYPE ??
    field.type ??
    "";
  return typeof raw === "string" ? raw.toUpperCase() : "";
};

const buildSegmentLabel = (segment: any): string => {
  if (!segment) return "";
  return (
    segment.name ??
    segment.code ??
    segment.segmentName ??
    segment.xpath ??
    `Segment ${segment.id ?? ""}`
  );
};

const getFieldName = (field: any): string => {
  if (!field) return "";
  return (
    field.name ??
    field.fieldName ??
    field.code ??
    `Field ${field.id ?? ""}`
  );
};

const getFieldPath = (field: any): string => {
  if (!field) return "";
  return field.fieldPath ?? field.xpath ?? "";
};

interface FieldSelectOption {
  value: number;
  label: string;
  fieldName: string;
  fieldPath: string;
  segmentLabel: string;
}

const toFieldSelectOption = (id: number, info: FieldInfo): FieldSelectOption => {
  const fieldName = getFieldName(info.field);
  const segmentLabel = buildSegmentLabel(info.segment);
  return {
    value: id,
    label: `${segmentLabel} – ${fieldName}`,
    fieldName,
    fieldPath: getFieldPath(info.field),
    segmentLabel,
  };
};

const FieldSelectOptionLabel: React.FC<
  OptionProps<FieldSelectOption, false, GroupBase<FieldSelectOption>>
> = (props) => (
  <components.Option {...props}>
    <div className="rec-select-option-title">{props.data.fieldName}</div>
    <small className="rec-select-segment">{props.data.segmentLabel}</small>
    {props.data.fieldPath ? (
      <code className="rec-select-path text-break mt-1">
        {props.data.fieldPath}
      </code>
    ) : null}
  </components.Option>
);

const FieldSelectSingleValue: React.FC<
  SingleValueProps<FieldSelectOption, false, GroupBase<FieldSelectOption>>
> = (props) => (
  <components.SingleValue {...props}>
    <span title={props.data.label}>{props.data.label}</span>
  </components.SingleValue>
);

const fieldSelectComponents = {
  Option: FieldSelectOptionLabel,
  SingleValue: FieldSelectSingleValue,
};

const filterFieldSelectOption = (
  option: { data: FieldSelectOption },
  input: string
): boolean => {
  const query = input.trim().toLowerCase();
  if (!query) return true;
  const { fieldName, fieldPath, segmentLabel } = option.data;
  return [fieldName, fieldPath, segmentLabel].some((part) =>
    part.toLowerCase().includes(query)
  );
};

const FieldSummary: React.FC<{
  title: string;
  fieldInfo: FieldInfo | undefined;
}> = ({ title, fieldInfo }) => {
  if (!fieldInfo) {
    return (
      <small className="rec-empty-hint d-block mt-2">
        {title}: No field selected
      </small>
    );
  }

  const { segment, field } = fieldInfo;
  const fieldName = getFieldName(field);
  const fieldPath = getFieldPath(field);

  return (
    <div className="rec-field-summary rec-field-summary--inline mt-2 p-2 rounded border">
      <div className="small rec-section-title mb-1">{title}</div>
      <div className="small">
        <span className="rec-field-label">Segment: </span>
        <span className="rec-field-value">{buildSegmentLabel(segment)}</span>
      </div>
      <div className="small mt-1">
        <span className="rec-field-label">Name: </span>
        <strong className="rec-field-value">{fieldName}</strong>
      </div>
      {fieldPath ? (
        <div className="small mt-1">
          <span className="rec-field-label d-block mb-1">Path:</span>
          <code className="rec-field-path user-select-all">{fieldPath}</code>
        </div>
      ) : null}
    </div>
  );
};

const RuleEngineConfig: React.FC = () => {
  const [protocolIdInput, setProtocolIdInput] = useState<string>("");
  const [activeProtocolId, setActiveProtocolId] = useState<number | null>(null);
  const [segments, setSegments] = useState<SegmentDto[]>([]);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [showUnmappedModal, setShowUnmappedModal] = useState<boolean>(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState<boolean>(false);
  const [scrollToMappingIndex, setScrollToMappingIndex] = useState<
    number | null
  >(null);
  const [showScrollTop, setShowScrollTop] = useState<boolean>(false);
  const mappingRowRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const menuPortalTarget =
    typeof document !== "undefined" ? document.body : undefined;

  const [urlParams] = useSearchParams();

  useEffect(() => {
    const protocolId = urlParams.get("protocolId") || "";
    if (protocolId) {
      setProtocolIdInput(protocolId);
    }
  }, [urlParams]);

  const fieldsIndexById: Map<number, FieldInfo> = useMemo(() => {
    const map = new Map<number, FieldInfo>();
    segments.forEach((s) => {
      const field = s.fields;
      if (!field) return;

      const fieldId: number | undefined =
        typeof field.id === "number"
          ? field.id
          : typeof field.fieldId === "number"
          ? field.fieldId
          : typeof field.FIELD_ID === "number"
          ? field.FIELD_ID
          : undefined;

      if (fieldId != null) {
        map.set(fieldId, { field, segment: s.segments });
      }
    });
    return map;
  }, [segments]);

  const allFieldsArray = useMemo(
    () =>
      Array.from(fieldsIndexById.entries()).map(([id, info]) => ({
        id,
        info,
      })),
    [fieldsIndexById]
  );

  const sourceFieldOptions = useMemo(
    () =>
      allFieldsArray
        .filter(({ info }) => getFieldType(info.field) === "SOURCE")
        .map(({ id, info }) => toFieldSelectOption(id, info)),
    [allFieldsArray]
  );

  const targetFieldOptions = useMemo(
    () =>
      allFieldsArray
        .filter(({ info }) => getFieldType(info.field) === "TARGET")
        .map(({ id, info }) => toFieldSelectOption(id, info)),
    [allFieldsArray]
  );

  const mappedTargetIds = useMemo(() => {
    const set = new Set<number>();
    fieldMappings.forEach((m) => {
      if (isInactiveMapping(m.mappingType)) return;
      if (usesSourceFieldAsTarget(m.mappingType)) return;
      if (typeof m.targetFieldId === "number") {
        set.add(m.targetFieldId);
      }
    });
    return set;
  }, [fieldMappings]);

  const duplicateTargetGroups = useMemo(() => {
    const indexesByTarget = new Map<number, number[]>();
    fieldMappings.forEach((m, index) => {
      if (isInactiveMapping(m.mappingType)) return;
      if (usesSourceFieldAsTarget(m.mappingType)) return;
      if (typeof m.targetFieldId !== "number") return;
      const list = indexesByTarget.get(m.targetFieldId) ?? [];
      list.push(index);
      indexesByTarget.set(m.targetFieldId, list);
    });

    return Array.from(indexesByTarget.entries())
      .filter(([, mappingIndexes]) => mappingIndexes.length > 1)
      .map(([targetFieldId, mappingIndexes]) => ({
        targetFieldId,
        mappingIndexes,
        fieldInfo: fieldsIndexById.get(targetFieldId),
      }));
  }, [fieldMappings, fieldsIndexById]);

  const duplicateTargetIds = useMemo(
    () => new Set(duplicateTargetGroups.map((g) => g.targetFieldId)),
    [duplicateTargetGroups]
  );

  const duplicateCountByTargetId = useMemo(() => {
    const map = new Map<number, number>();
    duplicateTargetGroups.forEach((g) => {
      map.set(g.targetFieldId, g.mappingIndexes.length);
    });
    return map;
  }, [duplicateTargetGroups]);

  const unmappedTargetFields = useMemo(() => {
    const mappedIds = new Set<number>();
    fieldMappings.forEach((m) => {
      if (isInactiveMapping(m.mappingType)) return;
      if (usesSourceFieldAsTarget(m.mappingType)) return;
      if (typeof m.targetFieldId === "number") {
        mappedIds.add(m.targetFieldId);
      }
    });

    return allFieldsArray
      .filter(({ info }) => getFieldType(info.field) === "TARGET")
      .filter(({ id }) => !mappedIds.has(id))
      .map(({ id, info }) => ({
        id,
        segment: info.segment,
        field: info.field,
      }));
  }, [allFieldsArray, fieldMappings]);

  useEffect(() => {
    if (saveMessage) {
      const timer = setTimeout(() => setSaveMessage(null), 3000);
      return () => clearTimeout(timer);
    }
    return;
  }, [saveMessage]);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 200);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (scrollToMappingIndex == null) return;
    const index = scrollToMappingIndex;
    const frame = requestAnimationFrame(() => {
      mappingRowRefs.current
        .get(index)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      setScrollToMappingIndex(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [fieldMappings, scrollToMappingIndex]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLoad = async () => {
    setError(null);
    setSaveMessage(null);

    const trimmed = protocolIdInput.trim();
    if (!trimmed) {
      setError("Please enter a protocol ID.");
      return;
    }

    const idNumber = Number(trimmed);
    if (!Number.isFinite(idNumber)) {
      setError("Protocol ID must be a number.");
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.get<SegmentsAndMappingsResponse>(
        `/api/query/manifest/protocols/${idNumber}/segments`
      );
      setActiveProtocolId(idNumber);
      setSegments(response.data.segments ?? []);
      setFieldMappings(
        (response.data.fieldMappings ?? []).map((m) => ({
          ...m,
          mappingType: m.mappingType
            ? m.mappingType.toUpperCase()
            : m.mappingType,
        }))
      );
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error("Failed to load protocol configuration", e);
      setError(
        e?.response?.data?.message ??
          "Failed to load protocol configuration. Please try again."
      );
      setSegments([]);
      setFieldMappings([]);
      setActiveProtocolId(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRow = () => {
    setScrollToMappingIndex(fieldMappings.length);
    setFieldMappings((prev) => [
      ...prev,
      {
        id: null,
        protocolId: activeProtocolId ?? null,
        sourceFieldId: null,
        targetFieldId: null,
        mappingType: "DIRECT",
        lookupType: "",
      },
    ]);
  };

  const updateMappingAt = <K extends keyof FieldMapping>(
    index: number,
    key: K,
    value: FieldMapping[K]
  ) => {
    setFieldMappings((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [key]: value } : row))
    );
  };

  const handleMappingTypeChange = (index: number, rawValue: string) => {
    const upper = (rawValue || "").toUpperCase();
    setFieldMappings((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const wasTargetAsSource = usesTargetAsSource(row.mappingType);
        let next: FieldMapping = {
          ...row,
          mappingType: upper,
        };
        if (usesTargetAsSource(upper) && typeof row.targetFieldId === "number") {
          next.sourceFieldId = row.targetFieldId;
        } else if (wasTargetAsSource && !usesTargetAsSource(upper)) {
          next.sourceFieldId = null;
        }
        if (
          usesSourceFieldAsTarget(upper) &&
          typeof next.targetFieldId === "number"
        ) {
          const targetInfo = fieldsIndexById.get(next.targetFieldId);
          if (getFieldType(targetInfo?.field) !== "SOURCE") {
            next.targetFieldId = null;
          }
        }
        return next;
      })
    );
  };

  const handleTargetFieldChange = (index: number, newId: number | null) => {
    setFieldMappings((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        let next: FieldMapping = {
          ...row,
          targetFieldId: newId,
        };
        if (usesTargetAsSource(row.mappingType) && typeof newId === "number") {
          next.sourceFieldId = newId;
        }
        return next;
      })
    );
  };

  const handleDeleteRow = (index: number) => {
    setFieldMappings((prev) => {
      const row = prev[index];
      if (row?.id != null) {
        return prev.map((r, i) =>
          i === index ? { ...r, mappingType: "INACTIVE" } : r
        );
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSave = async () => {
    if (!activeProtocolId) {
      setError("Please load a protocol before saving mappings.");
      return;
    }

    setError(null);
    setSaveMessage(null);
    setSaving(true);
    try {
      const payload: FieldMapping[] = fieldMappings
        .filter((row) => {
          if (isInactiveMapping(row.mappingType) && row.id != null) {
            return typeof row.targetFieldId === "number";
          }
          if (typeof row.targetFieldId !== "number") {
            return false;
          }
          if (usesTargetAsSource(row.mappingType)) {
            return true;
          }
          return typeof row.sourceFieldId === "number";
        })
        .map((row) =>
          syncSourceFromTarget({
            ...row,
            protocolId: activeProtocolId,
          })
        );

      const response = await axiosInstance.post<FieldMapping[]>(
        `/api/query/manifest/protocols/${activeProtocolId}/field-mappings`,
        payload
      );

      setFieldMappings(
        (response.data ?? []).map((m) => ({
          ...m,
          mappingType: m.mappingType
            ? m.mappingType.toUpperCase()
            : m.mappingType,
        }))
      );
      setSaveMessage("Field mappings saved successfully.");
      toast.success("Field mappings saved successfully.");
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error("Failed to save field mappings", e);
      setError(
        e?.response?.data?.message ??
          "Failed to save field mappings. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!event.altKey) return;

      const key = event.key.toLowerCase();

      if (key === "s") {
        event.preventDefault();
        void handleSave();
      } else if (key === "n") {
        event.preventDefault();
        handleAddRow();
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleSave]);

  return (
    <div className="container-fluid py-3 rec-root">
      <Card>
        <Card.Header>
          <Card.Title as="h5" className="mb-0">
            Rule Engine Protocol Field Mapping
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              void handleLoad();
            }}
          >
            <Row className="align-items-end g-2">
              <Col xs={12} md={4}>
                <Form.Group controlId="protocolId">
                  <Form.Label>Protocol ID</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter protocol ID"
                    value={protocolIdInput}
                    onChange={(e) => setProtocolIdInput(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md="auto">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading}
                  className="mt-3 mt-md-0"
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Loading...
                    </>
                  ) : (
                    "Load"
                  )}
                </Button>
              </Col>
              <Col xs={12} md="auto">
                <Button
                  variant="success"
                  type="button"
                  disabled={saving || !activeProtocolId}
                  className="mt-3 mt-md-0"
                  onClick={() => void handleSave()}
                >
                  {saving ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Saving...
                    </>
                  ) : (
                    "Save Mappings"
                  )}
                </Button>
              </Col>
            </Row>
          </Form>

          {error && (
            <Alert
              variant="danger"
              className="mt-3"
              onClose={() => setError(null)}
              dismissible
            >
              {error}
            </Alert>
          )}

          {saveMessage && (
            <Alert
              variant="success"
              className="mt-3"
              onClose={() => setSaveMessage(null)}
              dismissible
            >
              {saveMessage}
            </Alert>
          )}

          {activeProtocolId && (
            <>
              <hr className="my-4" />
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                  Protocol ID:{" "}
                  <strong>
                    {activeProtocolId}
                  </strong>
                  {"  "}
                  {unmappedTargetFields.length > 0 ? (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ms-2 align-baseline"
                      onClick={() => setShowUnmappedModal(true)}
                    >
                      Unmapped target fields: {unmappedTargetFields.length}
                    </Button>
                  ) : (
                    <small className="text-success ms-2">
                      All target fields mapped
                    </small>
                  )}
                  {duplicateTargetGroups.length > 0 ? (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ms-2 align-baseline rec-duplicate-hint"
                      onClick={() => setShowDuplicateModal(true)}
                    >
                      Duplicate target mappings: {duplicateTargetGroups.length}
                    </Button>
                  ) : null}
                </h6>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={handleAddRow}
                >
                  Add Mapping Row
                </Button>
              </div>

              <MappingTypeReference />

              {fieldMappings.length === 0 ? (
                <Alert variant="light" className="rec-empty-alert text-center mb-0">
                  No mappings yet. Click <strong>Add Mapping Row</strong> to
                  create one.
                </Alert>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {fieldMappings.map((row, index) => {
                    const isInactive = isInactiveMapping(row.mappingType);
                    const hideSource = usesTargetAsSource(row.mappingType);
                    const sourceInfo =
                      row.sourceFieldId != null
                        ? fieldsIndexById.get(row.sourceFieldId)
                        : undefined;
                    const targetInfo =
                      row.targetFieldId != null
                        ? fieldsIndexById.get(row.targetFieldId)
                        : undefined;

                    const targetFieldSelectOptions = usesSourceFieldAsTarget(
                      row.mappingType
                    )
                      ? sourceFieldOptions
                      : targetFieldOptions;

                    const availableTargetOptions = usesSourceFieldAsTarget(
                      row.mappingType
                    )
                      ? targetFieldSelectOptions
                      : targetFieldSelectOptions.filter(
                          (opt) =>
                            !mappedTargetIds.has(opt.value) ||
                            opt.value === row.targetFieldId
                        );

                    const sourceValue =
                      row.sourceFieldId != null
                        ? sourceFieldOptions.find(
                            (o) => o.value === row.sourceFieldId
                          ) ?? null
                        : null;

                    const targetValue =
                      row.targetFieldId != null
                        ? availableTargetOptions.find(
                            (o) => o.value === row.targetFieldId
                          ) ?? null
                        : null;

                    const isDuplicateTarget =
                      !isInactive &&
                      !usesSourceFieldAsTarget(row.mappingType) &&
                      typeof row.targetFieldId === "number" &&
                      duplicateTargetIds.has(row.targetFieldId??null);
                    const duplicateCount = isDuplicateTarget && row.targetFieldId
                      ? duplicateCountByTargetId.get(row.targetFieldId)
                      : undefined;

                    return (
                      <div
                        key={row.id ?? `new-${index}`}
                        ref={(el) => {
                          if (el) {
                            mappingRowRefs.current.set(index, el);
                          } else {
                            mappingRowRefs.current.delete(index);
                          }
                        }}
                      >
                      <Card
                        className={`border shadow-sm${
                          isInactive
                            ? " rec-mapping-card--inactive"
                            : isDuplicateTarget
                            ? " rec-mapping-card--duplicate-target"
                            : ""
                        }`}
                      >
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <span className="rec-mapping-index small">
                              Mapping {index + 1}
                              {isInactive ? (
                                <span className="badge bg-secondary ms-2">
                                  Inactive
                                </span>
                              ) : null}
                              {isDuplicateTarget ? (
                                <span
                                  className="badge bg-warning text-dark ms-2"
                                  title="This target field is mapped more than once"
                                >
                                  Duplicate target
                                  {duplicateCount != null
                                    ? ` (${duplicateCount})`
                                    : ""}
                                </span>
                              ) : null}
                            </span>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              disabled={isInactive}
                              onClick={() => handleDeleteRow(index)}
                            >
                              Delete
                            </Button>
                          </div>
                          <Row className="g-3 align-items-start rec-mapping-controls-row">
                            {!hideSource ? (
                              <Col xs={12} lg={4}>
                                <Form.Label className="rec-form-label fw-semibold small mb-1">
                                  Source field
                                </Form.Label>
                                <Select<FieldSelectOption>
                                  classNamePrefix={SELECT_CLASS_PREFIX}
                                  placeholder="Select source field"
                                  isClearable
                                  isDisabled={isInactive}
                                  menuPortalTarget={menuPortalTarget}
                                  menuPosition="fixed"
                                  styles={fieldSelectStyles}
                                  components={fieldSelectComponents}
                                  filterOption={filterFieldSelectOption}
                                  options={sourceFieldOptions}
                                  value={sourceValue}
                                  onChange={(selected) =>
                                    updateMappingAt(
                                      index,
                                      "sourceFieldId",
                                      selected ? selected.value : null
                                    )
                                  }
                                />
                                <FieldSummary
                                  title="Source"
                                  fieldInfo={sourceInfo}
                                />
                              </Col>
                            ) : null}
                            <Col xs={12} lg={hideSource ? 8 : 4}>
                              <Form.Label className="rec-form-label fw-semibold small mb-1">
                                Target field
                              </Form.Label>
                              <Select<FieldSelectOption>
                                classNamePrefix={SELECT_CLASS_PREFIX}
                                placeholder={
                                  usesSourceFieldAsTarget(row.mappingType)
                                    ? "Select source field"
                                    : "Select target field"
                                }
                                isClearable
                                isDisabled={isInactive}
                                menuPortalTarget={menuPortalTarget}
                                menuPosition="fixed"
                                styles={fieldSelectStyles}
                                components={fieldSelectComponents}
                                filterOption={filterFieldSelectOption}
                                options={availableTargetOptions}
                                value={targetValue}
                                onChange={(selected) =>
                                  handleTargetFieldChange(
                                    index,
                                    selected ? selected.value : null
                                  )
                                }
                              />
                              {hideSource ? (
                                <small className="rec-hint d-block mt-1">
                                  Source field is set to the same target field
                                  for {row.mappingType} mappings.
                                </small>
                              ) : usesSourceFieldAsTarget(row.mappingType) ? (
                                <small className="rec-hint d-block mt-1">
                                  Select a source field to compare against the
                                  source field for {row.mappingType} mappings.
                                </small>
                              ) : null}
                              {isDuplicateTarget ? (
                                <small className="rec-duplicate-hint d-block mt-1">
                                  This target field is mapped more than once.
                                  That can be valid, but is uncommon — check
                                  for a misconfiguration.
                                </small>
                              ) : null}
                              <FieldSummary
                                title="Target"
                                fieldInfo={targetInfo}
                              />
                            </Col>
                            <Col xs={12} lg={4} className="rec-mapping-config-col">
                              <Form.Group className="mb-2">
                                <Form.Label className="rec-form-label fw-semibold small mb-1">
                                  Mapping type
                                </Form.Label>
                                <MappingTypeSelect
                                  value={row.mappingType}
                                  menuPortalTarget={menuPortalTarget}
                                  onChange={(value) =>
                                    handleMappingTypeChange(index, value)
                                  }
                                />
                              </Form.Group>
                              <Form.Group className="mb-0">
                                <Form.Label className="rec-form-label fw-semibold small mb-1">
                                  Lookup type
                                </Form.Label>
                                <Form.Control
                                  size="sm"
                                  type="text"
                                  placeholder="Lookup type"
                                  disabled={isInactive}
                                  value={row.lookupType ?? ""}
                                  onChange={(e) =>
                                    updateMappingAt(
                                      index,
                                      "lookupType",
                                      e.target.value
                                    )
                                  }
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                      </div>
                    );
                  })}
                </div>
              )}

              <Modal
                show={showUnmappedModal}
                onHide={() => setShowUnmappedModal(false)}
                size="lg"
              >
                <Modal.Header closeButton>
                  <Modal.Title>
                    Unmapped Target Fields (Protocol {activeProtocolId})
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  {unmappedTargetFields.length === 0 ? (
                    <p className="mb-0">All target fields are mapped.</p>
                  ) : (
                    <Table bordered hover size="sm" className="mb-0">
                      <thead className="table-secondary">
                        <tr>
                          <th>Segment</th>
                          <th>Field name</th>
                          <th>Field path</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unmappedTargetFields.map((u) => (
                          <tr key={u.id}>
                            <td className="align-top">
                              {buildSegmentLabel(u.segment)}
                            </td>
                            <td className="align-top">
                              <strong>{getFieldName(u.field)}</strong>
                            </td>
                            <td className="align-top">
                              <code className="rec-modal-path">
                                {getFieldPath(u.field) || "—"}
                              </code>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowUnmappedModal(false)}>
                    Close
                  </Button>
                </Modal.Footer>
              </Modal>

              <Modal
                show={showDuplicateModal}
                onHide={() => setShowDuplicateModal(false)}
                size="lg"
              >
                <Modal.Header closeButton>
                  <Modal.Title>
                    Duplicate Target Mappings (Protocol {activeProtocolId})
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  {duplicateTargetGroups.length === 0 ? (
                    <p className="mb-0">No target field is mapped more than once.</p>
                  ) : (
                    <>
                      <p className="small rec-hint mb-3">
                        The same target field is mapped more than once. That can
                        be valid, but is uncommon — review these for possible
                        misconfiguration.
                      </p>
                      <Table bordered hover size="sm" className="mb-0">
                        <thead className="table-warning">
                          <tr>
                            <th>Segment</th>
                            <th>Field name</th>
                            <th>Field path</th>
                            <th>Mappings</th>
                          </tr>
                        </thead>
                        <tbody>
                          {duplicateTargetGroups.map((group) => (
                            <tr key={group.targetFieldId}>
                              <td className="align-top">
                                {buildSegmentLabel(group.fieldInfo?.segment)}
                              </td>
                              <td className="align-top">
                                <strong>
                                  {getFieldName(group.fieldInfo?.field) ||
                                    `Field ${group.targetFieldId}`}
                                </strong>
                              </td>
                              <td className="align-top">
                                <code className="rec-modal-path">
                                  {getFieldPath(group.fieldInfo?.field) || "—"}
                                </code>
                              </td>
                              <td className="align-top">
                                {group.mappingIndexes.map((mappingIndex, i) => (
                                  <React.Fragment key={mappingIndex}>
                                    {i > 0 ? ", " : null}
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="p-0 align-baseline"
                                      onClick={() => {
                                        setShowDuplicateModal(false);
                                        setScrollToMappingIndex(mappingIndex);
                                      }}
                                    >
                                      #{mappingIndex + 1}
                                    </Button>
                                  </React.Fragment>
                                ))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={() => setShowDuplicateModal(false)}
                  >
                    Close
                  </Button>
                </Modal.Footer>
              </Modal>
            </>
          )}
        </Card.Body>
      </Card>

      {activeProtocolId && showScrollTop ? (
        <Button
          type="button"
          variant="primary"
          size="sm"
          className="rec-scroll-top"
          aria-label="Scroll to top"
          title="Scroll to top"
          onClick={scrollToTop}
        >
          ↑
        </Button>
      ) : null}
    </div>
  );
};

export default RuleEngineConfig;


